//asian-paints-frontend/src/pages/admin/AdminDashboard.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import totalassure from "../../assets/totalassure.png";
import asianPaintsLogo from "../../assets/APlogo.png";
import API from "../../services/api";
import {
  Avatar,
  Card,
  DashboardShell,
  GhostButton,
  KpiCard,
  PrimaryButton,
  ProgressBar,
  SectionHeader,
  StatusBadge,
  palette,
} from "../../components/dashboard/DashboardKit";
import { FolderOpen, Settings, ClipboardList, Calendar, CheckCircle2, Hourglass, Archive, Home, MessageSquare } from "lucide-react";
const MONTSERRAT = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`;

// ─── Nav (UNCHANGED) ──────────────────────────────────────────────────────────
const adminNav = [
{ icon: <Home className="w-5 h-5" />, label: "Dashboard", path: "/admin-dashboard" },
{
    icon: <Settings className="w-5 h-5" />,
    label: "Masters",
    children: [
      { label: "User Master",  path: "/user-master"  },
      
      { label: "Phase Master", path: "/phase-master" },
      { label: "Task Master",  path: "/task-master"  },
      {label: "Report Master", path:"/report-category-master"},
      {label: "Segment Master", path:"/discussion-topic-master"},
      {label: "Mail Logs", path:"/mail-logs"}
    ],
  },
 {
    icon: <FolderOpen className="w-5 h-5" />,
    label: "Project Management",
    children: [
      { label: "Project List",  path: "/project-list"  },
      { label: "Project Add",   path: "/project-add"   },
      { label: "Project Approvals", path: "/project-approvals" },
      { label: "Reassign Projects", path: "/reassign-project" },
    ],
  },

 
 
{
    icon: <ClipboardList className="w-5 h-5" />,
    label: "Reports & Approvals",
    children: [
      { label: "Project Reports", path: "/project-reports" },
      { label: "Report Approval", path: "/report-approval" },
      { label: "Deleted Reports", path: "/deleted-reports" },
    ],
  },
  {
    icon: <MessageSquare className="w-5 h-5" />,
    label: "Client Feedback",
    path: "/feedback-log",
  },
{
    icon: <Calendar className="w-5 h-5" />,
    label: "Planning",
    children: [{ label: "Work Schedule", path: "/work-schedule" }],
  },
 
];

// ─── Helpers (UNCHANGED) ─────────────────────────────────────────────────────
const initials = (name = "U") =>
  name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2) || "U";

const tones = ["violet", "orange", "red", "gold", "mixed"];

const statusKey = (status = "") => {
  const v = String(status).toLowerCase();
  if (v.includes("complete") || v.includes("approved")) return "done";
  if (v.includes("delay")    || v.includes("reject"))   return "alert";
  if (v.includes("pending"))                             return "pending";
  if (v.includes("active"))                              return "active";
  return "progress";
};

// ─── Tooltip styles ───────────────────────────────────────────────────────────
const TooltipStyle = {
  contentStyle: {
    background: "#1E0A3C",
    border: "none",
    borderRadius: 10,
    fontSize: 12,
    color: "#E8DEF8",
  },
  itemStyle: { color: "#E8DEF8" },
  labelStyle: { color: "#C4B8DC", fontWeight: 700 },
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [filter,    setFilter]    = useState("All");
  const [overdueDigest, setOverdueDigest] = useState({ totalOverdue: 0, lastDigestSentDate: null, items: [] });
  const [dashboard, setDashboard] = useState({
    metrics:          {},
    projects:         [],
    team:             [],
    recentReports:    [],
    statusBreakdown:  [],
  });

  // ── UNCHANGED: same two API calls, same state setter ──────────────────────
 useEffect(() => {
 API.get("/admin/dashboard-status", { params: { _t: Date.now() } })
    .then((res) => {
      console.log("ADMIN RESPONSE", res.data);
      console.log("overduePhases raw:", res.data?.overduePhases);
      if (res.data?.success) setDashboard(res.data);
    })
    .catch((err) => console.error("Dashboard fetch failed", err));
}, []);

  useEffect(() => {
    API.get("/admin/overdue-summary", { params: { _t: Date.now() } })
      .then((res) => {
        if (res.data?.success) setOverdueDigest(res.data);
      })
      .catch((err) => console.error("Overdue digest fetch failed", err));
  }, []);

  // ── CHANGE 1: new KPI metrics pulled from same API response ───────────────
  // Old: pendingReports, delayedProjects, engineers, siteVisitsToday
  // New: totalProjects, activeProjects, pendingApprovals, archiveProjects
  // All keys read from dashboard — if backend doesn't send them yet they
  // gracefully fall back to 0. No API change needed.
  const metrics = {
    totalProjects:     dashboard.totalProjects     || dashboard.metrics?.totalProjects     || 0,
    activeProjects:    dashboard.activeProjects     || dashboard.metrics?.activeProjects     || 0,
    pendingApprovals:  dashboard.pendingApprovals   || dashboard.metrics?.pendingApprovals   ||
                       dashboard.awaitingApproval   || 0,
    archiveProjects:   dashboard.archiveProjects    || dashboard.metrics?.archiveProjects    || 0,
  };

  // ── CHANGE 2: overdue phases — read from same API, fallback to [] ─────────
 const overduePhases = (
  dashboard.overduePhases ||
  dashboard.overdue_phases ||
  []
).map((p) => ({
  project:  p.project_name || p.project_uid || `Project ${p.id}`,
  phase:    p.phase_name   || p.phase_id    || p.phase || "—",
  daysLate: Number(p.days_overdue || p.daysLate || 0),
  status:   p.status       || "Overdue",
}));
console.log("overduePhases raw:", dashboard.overduePhases);

  // ── CHANGE 2: overdue projects ────────────────────────────────────────────
  const overdueProjects = (
    dashboard.overdueProjects ||
    dashboard.overdue_projects ||
    []
  ).map((p) => ({
    id:       p.project_uid  || p.id,
    name:     p.project_name || p.title || "—",
    daysLate: Number(p.days_overdue || p.daysLate || 0),
    engineer: p.engineer_name || "Unassigned",
    status:   p.status || "Delayed",
  }));

  // ── CHANGE 2: region-wise project counts for bar chart ────────────────────
  const regionData = (
    dashboard.regionWiseProjects ||
    dashboard.region_wise_projects ||
    []
  ).map((r) => ({
    region: r.region || r.location || r.name || "—",
    total:  Number(r.total  || r.count       || 0),
    active: Number(r.active || r.activeCount || 0),
  }));

  // ── CHANGE 2: segment-wise project counts for pie/bar chart ──────────────
  const segmentData = (
    dashboard.segmentWiseProjects ||
    dashboard.segment_wise_projects ||
    []
  ).map((s, i) => ({
    name:  s.segment || s.service_type || s.name || "—",
    value: Number(s.total || s.count   || 0),
    color: [palette.violet, palette.orange, palette.red, "#1A7A42", "#C87800"][i % 5],
  }));

  // ── Project table (UNCHANGED logic, same data shape) ─────────────────────
  const projects = (dashboard.projectMonitoring || []).map((p, i) => ({
    id:          p.project_uid || p.id,
    phase:       p.project_name,
    status:      p.project_status,
    statusKey:   statusKey(p.project_status),
    engineer:    p.engineer_name || "Unassigned",
    initials:    initials(p.engineer_name || "U"),
    completion:  Number(p.progress || 0),
    priority:    p.priority || "Normal",
    priorityKey: statusKey(p.priority || "Normal"),
    tone:        tones[i % tones.length],
  }));

  const visibleProjects =
    filter === "All"
      ? projects
      : projects.filter((p) => p.status === filter);

  // ── Responsive style ──────────────────────────────────────────────────────
  const gridCols2 = "grid gap-3.5 md:grid-cols-2";

  console.log("OVERDUE", dashboard.overdueProjects);

  return (
    <DashboardShell
      portal="Admin Portal"
      roleLabel="ADMIN"
     title={
  <div className="flex items-center gap-6">
    <img
      src={totalassure}
      alt="TotalAssure"
      className="h-14 md:h-16 w-auto object-contain"
    />

    <img
      src={asianPaintsLogo}
      alt="Asian Paints"
      className="h-12 md:h-14 w-auto object-contain"
    />
  </div>
}
      subtitle=""
      navItems={adminNav}
      stats={[
        { label: "Projects", value: dashboard.metrics?.totalProjects || dashboard.totalProjects || 0, className: "text-ap-violetLight" },
        { label: "Users",    value: dashboard.metrics?.totalUsers    || dashboard.totalUsers    || 0, className: "text-ap-red"         },
        { label: "Reports",  value: dashboard.metrics?.totalReports  || dashboard.totalReports  || 0, className: "text-ap-orange"      },
      ]}
user={{
  initials: (localStorage.getItem("name") || "A").split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2),
  name: localStorage.getItem("name") || "Admin",
  role: (() => {
    const r = (localStorage.getItem("role") || "").toLowerCase();
    const t = (localStorage.getItem("type") || "").toLowerCase();
    if (t === "admin" || Number(localStorage.getItem("is_admin")) === 1) return "Administrator";
    if (r === "manager") return "Manager";
    if (r === "engineer") return "Engineer";
    return "Admin";
  })()
}}
    >
      <style>{MONTSERRAT + ` * { font-family: 'Montserrat', sans-serif !important; }`}</style>

      {/* ── CHANGE 1: NEW KPI CARDS ── */}
      {/* Old: Pending Approvals · Delayed Projects · Active Engineers · Site Visits */}
      {/* New: Total Projects · Active Projects · Pending Approvals · Archive Projects */}
     <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
          label="Total Projects"
          value={metrics.totalProjects}
          icon={<FolderOpen className="w-7 h-7" />}
          badge="All Time"
          tone="violet"
          onClick={() => navigate("/project-list")}
        />
<KpiCard
          label="Active Projects"
          value={metrics.activeProjects}
          icon={<CheckCircle2 className="w-7 h-7" />}
          badge="Live"
          tone="orange"
          onClick={() => navigate("/project-list?status=active")}
        />
<KpiCard
          label="Pending Approvals"
          value={metrics.pendingApprovals}
          icon={<Hourglass className="w-7 h-7" />}
          badge="Action Needed"
          tone="red"
          onClick={() => navigate("/project-approvals")}
        />
<KpiCard
  label="Archive Projects"
  value={metrics.archiveProjects}
  icon={
    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8"/>
      <rect x="1" y="3" width="22" height="5" rx="1"/>
      <line x1="12" y1="11" x2="12" y2="17"/>
      <polyline points="9 14 12 17 15 14"/>
    </svg>
  }
  badge="Closed"
  tone="gold"
  onClick={() => navigate("/project-list?status=archived")}
/>
      </div>

      {/* ── CHANGE 2: OVERDUE PHASES + OVERDUE PROJECTS ── */}
      <div className={`mt-4 ${gridCols2}`}>

        {/* Overdue Phases */}
        <Card>
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Overdue Phases"
              subtitle="Phases past their scheduled end date"
            />
            {overduePhases.length === 0 ? (
<div className="py-8 text-center text-xs text-ap-muted flex flex-col items-center gap-1">

                No overdue phases right now.
              </div>
            ) : (
              <div className="space-y-2 mt-3">
                {overduePhases.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 rounded-[11px] border border-ap-border bg-ap-surface px-3.5 py-3 transition hover:bg-ap-violet/10"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-bold text-ap-dark">{p.project}</div>
                      <div className="text-[10px] text-ap-muted mt-0.5">{p.phase}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="rounded-full bg-ap-redLight px-2.5 py-1 text-[10px] font-bold text-ap-red whitespace-nowrap">
                        +{p.daysLate}d late
                      </span>
                      <StatusBadge label={p.status} status="alert" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Overdue Projects */}
        <Card bar="red">
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Overdue Projects"
              subtitle="Projects past their scheduled delivery date"
            />
            {overdueProjects.length === 0 ? (
<div className="py-8 text-center text-xs text-ap-muted flex flex-col items-center gap-1">
                
                No overdue projects right now.
              </div>
            ) : (
              <div className="space-y-2 mt-3">
                {overdueProjects.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 rounded-[11px] border border-ap-border bg-ap-surface px-3.5 py-3 transition hover:bg-ap-violet/10"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-syne text-xs font-extrabold text-ap-violet">{p.id}</span>
                        <span className="truncate text-xs font-semibold text-ap-dark">{p.name}</span>
                      </div>
<div className="text-[10px] text-ap-muted mt-0.5 flex items-center gap-1">
<Settings className="w-4 h-4" /> {p.engineer}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="rounded-full bg-ap-redLight px-2.5 py-1 text-[10px] font-bold text-ap-red whitespace-nowrap">
                        +{p.daysLate}d
                      </span>
                      <StatusBadge label={p.status} status="alert" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── CHANGE 2: REGION-WISE + SEGMENT-WISE GRAPHS ── */}
      <div className={`mt-4 ${gridCols2}`}>

        {/* Region-wise Bar Chart */}
        <Card bar="mixed">
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Region-wise Projects"
              subtitle="Total vs active projects by region"
            />
            {regionData.length === 0 ? (
              <div className="py-8 text-center text-xs text-ap-muted">
                No region data available yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={regionData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  barCategoryGap="28%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(92,45,145,0.08)" vertical={false} />
                  <XAxis
                    dataKey="region"
                    tick={{ fill: "#6B5A8A", fontSize: 10, fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#6B5A8A", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip {...TooltipStyle} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: "#6B5A8A", paddingTop: 10 }}
                  />
                  <Bar dataKey="total"  name="Total"  fill={palette.violet} radius={[6,6,0,0]} maxBarSize={36} />
                  <Bar dataKey="active" name="Active" fill={palette.orange} radius={[6,6,0,0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Segment-wise Pie Chart */}
        <Card bar="orange">
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Segment-wise Projects"
              subtitle="Project count by service segment"
            />
            {segmentData.length === 0 ? (
              <div className="py-8 text-center text-xs text-ap-muted">
                No segment data available yet.
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={segmentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {segmentData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} strokeWidth={0} />
                      ))}
                    </Pie>
                    <Tooltip {...TooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Legend rows */}
                <div className="mt-3 space-y-1.5">
                  {segmentData.map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-sm shrink-0"
                          style={{ background: s.color }}
                        />
                        <span className="text-[11px] text-ap-muted truncate max-w-[160px]">{s.name}</span>
                      </div>
                      <span
                        className="font-syne text-[13px] font-extrabold"
                        style={{ color: s.color }}
                      >
                        {s.value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

            {/* ── OVERDUE DIGEST SUMMARY ── */}
      <div className="mt-4">
        <Card bar="red">
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Overdue Digest Status"
              subtitle={
                overdueDigest.lastDigestSentDate
                  ? `Last sent: ${new Date(overdueDigest.lastDigestSentDate).toLocaleDateString('en-IN')}`
                  : "No digest sent yet"
              }
            />
            <div className="flex items-center gap-3 mt-3">
              <span className="rounded-full bg-ap-redLight px-3 py-1.5 text-xs font-bold text-ap-red">
                {overdueDigest.totalOverdue} overdue item(s) currently
              </span>
            </div>
            {overdueDigest.items.length > 0 && (
              <div className="space-y-2 mt-3">
                {overdueDigest.items.slice(0, 5).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 rounded-[11px] border border-ap-border bg-ap-surface px-3.5 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-bold text-ap-dark">{item.project_name || item.project_uid || '—'}</div>
                      <div className="text-[10px] text-ap-muted mt-0.5">{item.phase_id || '—'} · {item.engineer_name || 'Unassigned'}</div>
                    </div>
                    <span className="rounded-full bg-ap-redLight px-2.5 py-1 text-[10px] font-bold text-ap-red whitespace-nowrap">
                      +{item.days_overdue}d late
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

    </DashboardShell>
  );
}